import { useState, useEffect, useRef, useLayoutEffect } from 'react'
import Select from 'react-select'
import { useSearchParams } from 'react-router-dom'
import CssApiClient from './Api'
import { useOktaAuth } from '@okta/okta-react'
import { useUser } from '../Context/UserContext'
import { ConstructionIcon, X } from 'lucide-react'

const SkeletonBox = ({ height = '40px' }) => (
  <div
    className='w-full rounded-lg bg-gray-200 animate-pulse'
    style={{ height }}
  />
)

const CustomNestedDropdown = ({
  groupedOptions,
  selectedCss,
  setSelectedCss,
  placeholder = 'All',
  openInitially = false,
  setBatchNumber,
  setHasMoreServiceRecords,
  setCaregiverDetails,
  setCaregiverBatchNumber,
  setSelectedRecord,
  disabled = false
}) => {
  const wrapperRef = useRef(null)
  const outerRef = useRef(null)
  const itemScrollRefs = useRef({})
  const scrollPositions = useRef({ outer: 0, inner: {} })

  const [open, setOpen] = useState(openInitially)
  const [expanded, setExpanded] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    const onDocClick = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
        setExpanded(null)
        setSearchTerm('')
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    if (outerRef.current) {
      outerRef.current.scrollTop = scrollPositions.current.outer || 0
    }
    if (expanded && itemScrollRefs.current[expanded]) {
      itemScrollRefs.current[expanded].scrollTop =
        scrollPositions.current.inner[expanded] || 0
    }
  }, [open, expanded])

  const toggleExpand = state => {
    scrollPositions.current.outer = outerRef.current?.scrollTop || 0
    if (expanded && itemScrollRefs.current[expanded]) {
      scrollPositions.current.inner[expanded] =
        itemScrollRefs.current[expanded].scrollTop || 0
    }
    setExpanded(prev => (prev === state ? null : state))
  }

    const handleSelectAll = (caregivers, stateValue) => {
      if (disabled) return
      scrollPositions.current.outer = outerRef.current?.scrollTop || 0
      if (itemScrollRefs.current[stateValue]) {
        scrollPositions.current.inner[stateValue] =
          itemScrollRefs.current[stateValue].scrollTop || 0
      }

      const allSelected = caregivers.every(cg =>
        selectedCss.some(sel => sel.value === cg.value)
      )
      if (allSelected) {
        setSelectedCss(prev => prev.filter(item => item.state !== stateValue))
      } else {
        const currentValues = new Set(selectedCss.map(s => s.value))
        const newOnes = caregivers.filter(cg => !currentValues.has(cg.value))
        setSelectedCss(prev => [...prev, ...newOnes])
      }

      setBatchNumber(1)
      setHasMoreServiceRecords(true)
      setCaregiverDetails([])
      setCaregiverBatchNumber(1)
      setSelectedRecord(null)
    }

  const handleCheckboxChange = (cg, stateValue, checked) => {
    if (disabled) return
    if (expanded && itemScrollRefs.current[expanded]) {
      scrollPositions.current.inner[expanded] =
        itemScrollRefs.current[expanded].scrollTop || 0
    }
    scrollPositions.current.outer = outerRef.current?.scrollTop || 0

    if (checked) {
      setSelectedCss(prev => [...prev, cg])
    } else {
      setSelectedCss(prev => prev.filter(item => item.value !== cg.value))
    }
    setBatchNumber(1)
    setHasMoreServiceRecords(true)
    setCaregiverDetails([])
    setCaregiverBatchNumber(1)
    setSelectedRecord(null)
  }

  const clearAll = e => {
    if (disabled) return
    e.stopPropagation()
    setSelectedCss([])
    setBatchNumber(1)
    setHasMoreServiceRecords(true)
    setCaregiverDetails([])
    setCaregiverBatchNumber(1)
    setSelectedRecord(null)
  }

  const selectedCount = selectedCss.length
  const filteredGroups = groupedOptions
    .map(group => {
      if (!searchTerm.trim()) return group
      const filteredCaregivers = group.caregivers.filter(cg =>
        cg.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
      return { ...group, caregivers: filteredCaregivers }
    })
    .filter(group => group.caregivers.length > 0)

  return (
    <div ref={wrapperRef} className='relative inline-block w-full'>
      <div
        role='button'
        tabIndex={0}
        className={`w-full border rounded-md px-3 py-2 flex justify-between items-center ${
          disabled
            ? 'cursor-not-allowed bg-gray-100 text-gray-400'
            : 'cursor-pointer'
        }`}
        onClick={() => !disabled && setOpen(prev => !prev)}
        onKeyDown={e => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            setOpen(prev => !prev)
          }
        }}
      >
        <div className='text-sm'>
          {selectedCount > 0 ? `${selectedCount} selected` : placeholder}
        </div>
        <div className='flex items-center space-x-2'>
          {selectedCount > 0 && !disabled && (
            <button
              onClick={clearAll}
              className='text-gray-400 hover:text-gray-600 focus:outline-none'
            >
              <X className='w-4 h-4' />
            </button>
          )}
          <span className='text-sm text-gray-400'>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && !disabled && (
        <div
          className='absolute left-0 right-0 mt-2 bg-white border rounded-md shadow-lg z-50'
          style={{ maxWidth: '100%' }}
        >
          <div className='p-2 border-b'>
            <div className='relative'>
              <input
                type='text'
                placeholder='Search CSS...'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className='w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500'
              />
              {searchTerm && (
                <button
                  type='button'
                  onClick={() => setSearchTerm('')}
                  className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none'
                >
                  <X className='w-4 h-4' />
                </button>
              )}
            </div>
          </div>

          <div
            ref={outerRef}
            style={{ maxHeight: 320, overflowY: 'auto' }}
            className='p-1'
          >
            {filteredGroups.length === 0 && (
              <div className='p-3 text-gray-500 text-sm text-center'>
                No CSS found
              </div>
            )}

            {filteredGroups.map(group => {
              const selectedCountInGroup = group.caregivers.filter(cg =>
                selectedCss.some(s => s.value === cg.value)
              ).length
              const allSelected =
                group.caregivers.length > 0 &&
                selectedCountInGroup === group.caregivers.length

              return (
                <div key={group.value} className='px-2 py-1'>
                  <div className='flex justify-between items-center'>
                    <label className='flex items-center space-x-2 cursor-pointer'>
                      <input
                        type='checkbox'
                        checked={allSelected}
                        onChange={() =>
                          handleSelectAll(group.caregivers, group.value)
                        }
                        className='mr-2'
                        disabled={disabled}
                      />
                      <span
                        className='font-medium'
                        onClick={() => !disabled && toggleExpand(group.value)}
                      >
                        {group.label}
                      </span>
                    </label>

                    <div className='flex items-center space-x-2'>
                      {selectedCountInGroup > 0 && (
                        <span className='bg-blue-500 text-white text-xs font-semibold rounded-full px-2 py-0.5'>
                          {selectedCountInGroup}
                        </span>
                      )}
                      <button
                        className='text-sm text-gray-600'
                        onClick={e => {
                          e.stopPropagation()
                          if (!disabled) toggleExpand(group.value)
                        }}
                        disabled={disabled}
                      >
                        {expanded === group.value ? '▲' : '▼'}
                      </button>
                    </div>
                  </div>

                  {expanded === group.value && group.caregivers.length > 0 && (
                    <div
                      ref={el => (itemScrollRefs.current[group.value] = el)}
                      className='ml-6 mt-2'
                      style={{ maxHeight: 220, overflowY: 'auto' }}
                      onClick={e => e.stopPropagation()}
                    >
                      {group.caregivers.map(cg => {
                        const checked = selectedCss.some(
                          s => s.value === cg.value
                        )
                        return (
                          <label
                            key={cg.value}
                            className={`flex items-center py-1 px-1 rounded ${
                              checked ? 'text-blue-600 font-medium' : ''
                            }`}
                          >
                            <input
                              type='checkbox'
                              checked={checked}
                              onChange={e =>
                                handleCheckboxChange(
                                  cg,
                                  group.value,
                                  e.target.checked
                                )
                              }
                              className='mr-2'
                              disabled={disabled}
                            />
                            <span className='truncate'>{cg.label}</span>
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

const WorkBenchFilters = ({
  selectedMonth,
  setSelectedMonth,
  selectedCss,
  setSelectedCss,
  selectedStatus,
  setSelectedStatus,
  setBatchNumber,
  setHasMoreServiceRecords,
  setFiltersInitialized,
  setCaregiverDetails,
  setCaregiverBatchNumber,
  setSelectedRecord,
  role,
  setRole
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [monthOptions, setMonthOptions] = useState([])
  const [stateOptions, setStateOptions] = useState([])
  const [caregiverOptions, setCaregiverOptions] = useState({})
  const [statusOptions, setStatusOptions] = useState([])
  const [loading, setLoading] = useState(true)

  const { oktaAuth, authState } = useOktaAuth()
  const userInfo = useUser()
  const userRole = userInfo?.job_profile?.toLowerCase()

  useEffect(() => {
    const month = searchParams.get('month')
    const status = searchParams.get('status')
    const css = searchParams.get('css')
    if (month) setSelectedMonth(month)
    if (status) setSelectedStatus(status)
    if (css) {
      const cssValues = css.split(',').map(item => {
        const [state, name] = item.split(':')
        return {
          value: name || item,
          label: name || item,
          state: state || ''
        }
      })
      setSelectedCss(cssValues)
    }

    const fetchData = async () => {
      try {
        if (!authState?.isAuthenticated) return
        setLoading(true)
        const data = await CssApiClient.postDropdown(oktaAuth, {})
        if (data) {
          const states = Object.keys(data.css || {}).map(state => ({
            value: state,
            label: state
          }))
          setStateOptions(states)

          const caregivers = Object.fromEntries(
            Object.entries(data.css || {}).map(([state, caregivers]) => [
              state,
              caregivers.map(name => ({ value: name, label: name, state }))
            ])
          )
          setCaregiverOptions(caregivers)
          setRole(data?.roles)
          setMonthOptions([
            ...(data.month_year || []).map(m => ({ value: m, label: m }))
          ])
          if (!selectedMonth && data.month_year?.length > 0) {
            setSelectedMonth(data.month_year[0])
          }
          setStatusOptions([
            { value: '', label: 'All' },
            ...(data.service_status || []).map(s => ({ value: s, label: s }))
          ])
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
    setFiltersInitialized(true)
    setBatchNumber(1)
  }, [authState, oktaAuth])

  useEffect(() => {
    const params = {}
    if (selectedMonth) params.month = selectedMonth
    if (selectedStatus) params.status = selectedStatus
    if (selectedCss.length > 0) {
      params.css = selectedCss.map(o => `${o.state}:${o.value}`).join(',')
    }
    setSearchParams(params)
  }, [selectedMonth, selectedStatus, selectedCss])

  const groupedOptions = stateOptions.map(state => ({
    ...state,
    caregivers: caregiverOptions[state.value] || []
  }))

  return (
    <div className='container mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8'>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            Month
          </label>
          {loading ? (
            <SkeletonBox />
          ) : (
            <Select
              options={monthOptions}
              value={
                monthOptions.find(m => m.value === selectedMonth) ||
                monthOptions.find(m => m.value === '')
              }
              onChange={option => {
                setSelectedMonth(option?.value || '')
                setBatchNumber(1)
                setHasMoreServiceRecords(true)
                setCaregiverDetails([])
                setCaregiverBatchNumber(1)
                setSelectedRecord(null)
              }}
              placeholder='Select month'
              className='text-sm'
              isSearchable
              menuPortalTarget={document.body}
              styles={{
                control: base => ({
                  ...base,
                  borderRadius: '0.5rem',
                  borderColor: '#e5e7eb',
                  boxShadow: 'none',
                  '&:hover': { borderColor: '#3b82f6' }
                }),
                menu: base => ({
                  ...base,
                  zIndex: 9999,
                  borderRadius: '0.5rem'
                }),
                menuPortal: base => ({ ...base, zIndex: 9999 })
              }}
            />
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            Select State / CSS
          </label>
          {loading ? (
            <SkeletonBox />
          ) : (
            <CustomNestedDropdown
              groupedOptions={groupedOptions}
              selectedCss={selectedCss}
              setSelectedCss={setSelectedCss}
              placeholder='All'
              setBatchNumber={setBatchNumber}
              setHasMoreServiceRecords={setHasMoreServiceRecords}
              setCaregiverDetails={setCaregiverDetails}
              setCaregiverBatchNumber={setCaregiverBatchNumber}
              setSelectedRecord={setSelectedRecord}
              disabled={role.includes(userRole)}
            />
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            Service Status
          </label>
          {loading ? (
            <SkeletonBox />
          ) : (
            <Select
              options={statusOptions}
              value={
                statusOptions.find(s => s.value === selectedStatus) ||
                statusOptions.find(s => s.value === '')
              }
              onChange={option => {
                setSelectedStatus(option?.value || '')
                setBatchNumber(1)
                setHasMoreServiceRecords(true)
                setCaregiverDetails([])
                setCaregiverBatchNumber(1)
                setSelectedRecord(null)
              }}
              placeholder='Select status'
              className='text-sm'
              isSearchable
              isClearable
              menuPortalTarget={document.body}
              styles={{
                control: base => ({
                  ...base,
                  borderRadius: '0.5rem',
                  borderColor: '#e5e7eb',
                  boxShadow: 'none',
                  '&:hover': { borderColor: '#3b82f6' }
                }),
                menu: base => ({
                  ...base,
                  zIndex: 9999,
                  borderRadius: '0.5rem'
                }),
                menuPortal: base => ({ ...base, zIndex: 9999 })
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default WorkBenchFilters
